import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Loader2, Check } from 'lucide-react';
import cultivarService from '../../services/cultivar';

interface CropType {
  id: number;
  name: string;
  description?: string | null;
  parent_id?: number | null;
}

interface CropTypeComboboxProps {
  /** Currently selected crop type ID */
  value: number | null | undefined;
  onChange: (id: number) => void;
  error?: string;
  disabled?: boolean;
}

function CropTypeCombobox({ value, onChange, error, disabled }: CropTypeComboboxProps) {
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Load all crop types once on mount
  useEffect(() => {
    cultivarService.getCropTypes().then(res => {
      if (res.success && res.data) setCropTypes(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const selected = cropTypes.find(ct => ct.id === value);
  const parentNameById = (id?: number | null) =>
    id ? cropTypes.find(ct => ct.id === id)?.name : undefined;

  const filtered = search.trim()
    ? cropTypes.filter(ct => ct.name.toLowerCase().includes(search.toLowerCase()))
    : cropTypes;

  // When not searching, render parents with children grouped beneath.
  // When searching, render a flat list of matches (with parent hint on children).
  const groupedDisplay = !search.trim();
  const parents = cropTypes
    .filter(ct => !ct.parent_id)
    .sort((a, b) => a.name.localeCompare(b.name));
  const childrenOf = (parentId: number) =>
    cropTypes
      .filter(ct => ct.parent_id === parentId)
      .sort((a, b) => a.name.localeCompare(b.name));

  const exactMatch = cropTypes.some(
    ct => ct.name.toLowerCase() === search.trim().toLowerCase()
  );
  const showAddNew = search.trim().length > 0 && !exactMatch && !adding;

  const handleSelect = (ct: CropType) => {
    onChange(ct.id);
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = async () => {
    const name = search.trim();
    if (!name) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await cultivarService.createCropType(name);
      if (res.success && res.data) {
        setCropTypes(prev => [...prev, res.data!].sort((a, b) => a.name.localeCompare(b.name)));
        onChange(res.data.id);
        setOpen(false);
        setSearch('');
      } else {
        setAddError('Could not add crop type');
      }
    } catch {
      setAddError('Could not add crop type');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between border rounded-lg px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white transition-colors ${
          error
            ? 'border-red-300 focus:ring-red-400'
            : 'border-neutral-300 hover:border-neutral-400'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={selected ? 'text-neutral-900' : 'text-neutral-400'}>
          {loading
            ? 'Loading…'
            : selected
              ? (parentNameById(selected.parent_id)
                  ? `${parentNameById(selected.parent_id)} › ${selected.name}`
                  : selected.name)
              : 'Select crop type…'}
        </span>
        {loading
          ? <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
          : <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        }
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg">
          {/* Search */}
          <div className="p-2 border-b border-neutral-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showAddNew) handleAddNew();
                if (e.key === 'Escape') { setOpen(false); setSearch(''); }
              }}
              placeholder="Search or type new crop…"
              className="w-full text-sm px-2 py-1.5 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Options list */}
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && !showAddNew && (
              <p className="text-sm text-neutral-400 text-center py-4">
                {search.trim() ? 'No match — press Enter to add' : 'Type to search or add a crop type'}
              </p>
            )}

            {groupedDisplay
              ? parents.map(parent => {
                  const kids = childrenOf(parent.id);
                  return (
                    <div key={parent.id}>
                      <button
                        type="button"
                        onMouseDown={() => handleSelect(parent)}
                        className="w-full flex items-center justify-between text-left px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-primary-50 hover:text-primary-700"
                      >
                        <span>{parent.name}</span>
                        {parent.id === value && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
                      </button>
                      {kids.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={() => handleSelect(c)}
                          className="w-full flex items-center justify-between text-left pl-7 pr-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                        >
                          <span>{c.name}</span>
                          {c.id === value && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  );
                })
              : filtered.map(ct => {
                  const parentName = parentNameById(ct.parent_id);
                  return (
                    <button
                      key={ct.id}
                      type="button"
                      onMouseDown={() => handleSelect(ct)}
                      className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-neutral-800 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span>
                        {parentName && (
                          <span className="text-neutral-400">{parentName} › </span>
                        )}
                        {ct.name}
                      </span>
                      {ct.id === value && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
                    </button>
                  );
                })}

            {/* Add new option */}
            {showAddNew && (
              <button
                type="button"
                onMouseDown={handleAddNew}
                disabled={adding}
                className="w-full flex items-center gap-1.5 text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 border-t border-neutral-100 font-medium"
              >
                {adding
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Plus className="h-3.5 w-3.5" />
                }
                {adding ? 'Adding…' : <>Add "<span>{search.trim()}</span>"</>}
              </button>
            )}
          </div>

          {addError && (
            <p className="text-xs text-red-600 px-3 py-2 border-t border-neutral-100">{addError}</p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default CropTypeCombobox;
